import React, { useMemo } from 'react';
import { History, LockKeyhole } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth, type User } from '../../context/AuthContext';

const DAY_MS = 24 * 60 * 60 * 1000;

export interface PaidDowngradeRecoveryBannerState {
  phase: 'recoverable' | 'expired';
  date: string | null;
  daysRemaining: number;
}

export const resolvePaidDowngradeRecoveryBannerState = (
  user: User | null,
  now = Date.now(),
): PaidDowngradeRecoveryBannerState | null => {
  const recovery = user?.paidDowngradeRecovery;
  if (!recovery || user?.isAdmin || user?.programCourtesy) return null;
  if (recovery.phase === 'unavailable') return null;
  if (recovery.phase === 'expired') {
    return { phase: 'expired', date: null, daysRemaining: 0 };
  }

  const timestamp = Date.parse(recovery.recoveryEndsAt || '');
  if (!Number.isFinite(timestamp) || timestamp <= now) return null;
  return {
    phase: 'recoverable',
    date: recovery.recoveryEndsAt,
    daysRemaining: Math.max(1, Math.ceil((timestamp - now) / DAY_MS)),
  };
};

const formatDate = (value: string, locale: string) => new Intl.DateTimeFormat(locale, {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
}).format(new Date(value));

const PaidDowngradeRecoveryBanner: React.FC = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const state = useMemo(() => resolvePaidDowngradeRecoveryBannerState(user), [user]);

  if (!state) return null;

  const locale = String(i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('fr')
    ? 'fr-CA'
    : 'en-CA';
  const Icon = state.phase === 'recoverable' ? History : LockKeyhole;
  const date = state.date ? formatDate(state.date, locale) : null;

  return (
    <aside
      aria-live="polite"
      aria-label={t('paidDowngradeBannerLabel')}
      style={{
        width: '100%',
        boxSizing: 'border-box',
        borderBottom: '1px solid var(--border-primary)',
        background: 'var(--warning-50, var(--card-bg))',
        color: 'var(--text-primary)',
      }}
    >
      <div
        style={{
          maxWidth: '1440px',
          minWidth: 0,
          margin: '0 auto',
          padding: '10px clamp(16px, 3vw, 32px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '10px 16px',
        }}
      >
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <Icon
            size={18}
            aria-hidden="true"
            style={{ flexShrink: 0, marginTop: '2px', color: 'var(--warning-700, var(--primary-500))' }}
          />
          <div style={{ minWidth: 0 }}>
            <strong style={{ display: 'block', fontSize: '14px', lineHeight: 1.35 }}>
              {t(`paidDowngradeBanner.${state.phase}.title`)}
            </strong>
            <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.45 }}>
              {t(`paidDowngradeBanner.${state.phase}.description`, {
                count: state.daysRemaining,
                date,
              })}
            </span>
          </div>
        </div>
        <Link
          to="/pricing"
          style={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: '36px',
            padding: '0 12px',
            borderRadius: '8px',
            background: 'var(--primary-500)',
            color: '#fff',
            fontSize: '13px',
            fontWeight: 700,
            textDecoration: 'none',
          }}
        >
          {t('paidDowngradeBanner.reviewPlans')}
        </Link>
      </div>
    </aside>
  );
};

export default PaidDowngradeRecoveryBanner;
